import { QueryClient, focusManager, onlineManager } from '@tanstack/react-query'

/**
 * Creates a pre-configured QueryClient instance for the application.
 *
 * Default configuration:
 * - staleTime: 5 minutes (data is considered fresh for 5 minutes)
 * - gcTime: 30 minutes (unused cache entries are garbage collected after 30 minutes)
 * - retry: 1 (one automatic retry on failure)
 * - refetchOnWindowFocus: false (no automatic refetch on tab focus)
 * - networkMode: 'online' — mutations/queries only run when the browser reports
 *   an active connection. This is intentionally NOT 'offlineFirst' to prevent
 *   mutations (writes) from being queued unintentionally while offline.
 *
 * These defaults can be overridden per-query via the `useQuery` options.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,   // 5 minutes
        gcTime: 30 * 60 * 1000,      // 30 minutes
        retry: 1,
        refetchOnWindowFocus: false,
        networkMode: 'online',
      },
      mutations: {
        networkMode: 'online',
      },
    },
  })
}

// ---------------------------------------------------------------------------
// Online / Focus Managers — configure for mobile browser behaviour
// ---------------------------------------------------------------------------

/**
 * Wire the React Query onlineManager to the browser's native online/offline
 * events. This provides faster and more reliable online detection than the
 * default polling strategy, which is important on mobile where connectivity
 * can change frequently.
 *
 * Called once from QueryProvider (client-side only).
 */
export function setupOnlineManager(): void {
  if (typeof window === 'undefined') return

  onlineManager.setEventListener((setOnline) => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  })
}

/**
 * Wire the React Query focusManager to the Page Visibility API.
 * On mobile, apps are frequently backgrounded; visibilitychange is more
 * reliable than the default window focus/blur event.
 *
 * Called once from QueryProvider (client-side only).
 */
export function setupFocusManager(): void {
  if (typeof window === 'undefined') return

  focusManager.setEventListener((setFocused) => {
    const handleVisibilityChange = () => {
      setFocused(document.visibilityState === 'visible')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  })
}

// ---------------------------------------------------------------------------
// Query Key Factory
// ---------------------------------------------------------------------------

/**
 * Centralised query key factory for consistent cache management.
 *
 * Usage:
 *   queryKey: queryKeys.tickets.list({ status: 'open' })
 *   queryKey: queryKeys.tickets.detail(ticketId)
 *   queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all })
 */
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => ['auth', 'me'] as const,
  },

  // Tickets / Support
  tickets: {
    all: ['tickets'] as const,
    list: (filters?: Record<string, unknown>) => ['tickets', 'list', filters] as const,
    detail: (id: number | string) => ['tickets', 'detail', id] as const,
  },

  // Team
  team: {
    all: ['team'] as const,
    members: (filters?: Record<string, unknown>) => ['team', 'members', filters] as const,
    groups: () => ['team', 'groups'] as const,
    invitations: () => ['team', 'invitations'] as const,
    permissions: () => ['team', 'permissions'] as const,
    activity: (filters?: Record<string, unknown>) => ['team', 'activity', filters] as const,
    analytics: (filters?: Record<string, unknown>) => ['team', 'analytics', filters] as const,
    availability: () => ['team', 'availability'] as const,
  },

  // CRM
  contacts: {
    all: ['contacts'] as const,
    list: (filters?: Record<string, unknown>) => ['contacts', 'list', filters] as const,
    detail: (id: string) => ['contacts', 'detail', id] as const,
    stats: () => ['contacts', 'stats'] as const,
    mergeCandidates: () => ['contacts', 'merge-candidates'] as const,
  },

  leads: {
    all: ['leads'] as const,
    list: (filters?: Record<string, unknown>) => ['leads', 'list', filters] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
  },

  deals: {
    all: ['deals'] as const,
    list: (filters?: Record<string, unknown>) => ['deals', 'list', filters] as const,
    detail: (id: string) => ['deals', 'detail', id] as const,
    timeline: (id: string, page?: number) => ['deals', 'timeline', id, page] as const,
    checklist: (id: string) => ['deals', 'checklist', id] as const,
    threads: (id: string) => ['deals', 'threads', id] as const,
    attachments: (id: string) => ['deals', 'attachments', id] as const,
    appointments: (id: string, filters?: Record<string, unknown>) => ['deals', 'appointments', id, filters] as const,
    tasks: (id: string) => ['deals', 'tasks', id] as const,
  },

  // DSGVO
  dsgvo: {
    all: ['dsgvo'] as const,
    dataPolicies: (filters?: Record<string, unknown>) => ['dsgvo', 'data-policies', filters] as const,
    dataMap: () => ['dsgvo', 'data-map'] as const,
    dsars: (filters?: Record<string, unknown>) => ['dsgvo', 'dsars', filters] as const,
    dsarDetail: (id: string) => ['dsgvo', 'dsar', id] as const,
    consent: () => ['dsgvo', 'consent'] as const,
    erasure: (filters?: Record<string, unknown>) => ['dsgvo', 'erasure', filters] as const,
    processingRecords: () => ['dsgvo', 'processing-records'] as const,
    breaches: (filters?: Record<string, unknown>) => ['dsgvo', 'breaches', filters] as const,
    privacyDashboard: () => ['dsgvo', 'privacy-dashboard'] as const,
  },

  // Security
  security: {
    all: ['security'] as const,
    sessions: () => ['security', 'sessions'] as const,
    alerts: (filters?: Record<string, unknown>) => ['security', 'alerts', filters] as const,
    apiKeys: () => ['security', 'api-keys'] as const,
    hardening: () => ['security', 'hardening'] as const,
    ipRestrictions: () => ['security', 'ip-restrictions'] as const,
    twoFactor: () => ['security', 'two-factor'] as const,
  },

  // Documents
  documents: {
    all: ['documents'] as const,
    list: (filters?: Record<string, unknown>) => ['documents', 'list', filters] as const,
    detail: (id: string) => ['documents', 'detail', id] as const,
    pendingApprovals: (filters?: Record<string, unknown>) => ['documents', 'pending-approvals', filters] as const,
    pendingCount: () => ['documents', 'pending-count'] as const,
    stats: () => ['documents', 'stats'] as const,
  },

  // Threads / Conversations
  threads: {
    all: ['threads'] as const,
    list: (filters?: Record<string, unknown>) => ['threads', 'list', filters] as const,
    detail: (id: string) => ['threads', 'detail', id] as const,
    approvals: (id: string) => ['threads', 'approvals', id] as const,
  },

  // Inbox (uses threads under the hood but with inbox-specific filters)
  inbox: {
    all: ['inbox'] as const,
    list: (filters?: Record<string, unknown>) => ['inbox', 'list', filters] as const,
  },

  // Usage & Billing
  usage: {
    all: ['usage'] as const,
    current: () => ['usage', 'current'] as const,
    plan: () => ['usage', 'plan'] as const,
  },

  // CRM Stats
  crm: {
    stats: () => ['crm', 'stats'] as const,
  },

  // Portal Management (P4)
  portal: {
    all: ['portal'] as const,
    links: (filters?: Record<string, unknown>) => ['portal', 'links', filters] as const,
    linkDetail: (id: string) => ['portal', 'link', id] as const,
    linkStats: (id: string) => ['portal', 'link-stats', id] as const,
    accessLogs: (filters?: Record<string, unknown>) => ['portal', 'access-logs', filters] as const,
    qnaThreads: (filters?: Record<string, unknown>) => ['portal', 'qna-threads', filters] as const,
    qnaDetail: (id: string) => ['portal', 'qna', id] as const,
    assets: (dealId: string) => ['portal', 'assets', dealId] as const,
  },

  // Integrations (P5)
  integrations: {
    all: ['integrations'] as const,
    google: () => ['integrations', 'google'] as const,
    googleStatus: () => ['integrations', 'google-status'] as const,
    telegram: () => ['integrations', 'telegram'] as const,
    crm: () => ['integrations', 'crm'] as const,
    imap: () => ['integrations', 'imap'] as const,
    calendar: () => ['integrations', 'calendar'] as const,
  },

  driveTargets: {
    all: ['drive-targets'] as const,
    list: () => ['drive-targets', 'list'] as const,
    detail: (id: string) => ['drive-targets', 'detail', id] as const,
  },

  // Compliance / Privacy (P6)
  compliance: {
    all: ['compliance'] as const,
    dashboard: () => ['compliance', 'dashboard'] as const,
    score: () => ['compliance', 'score'] as const,
    quickActions: () => ['compliance', 'quick-actions'] as const,
    timeline: (filters?: Record<string, unknown>) => ['compliance', 'timeline', filters] as const,
  },

  // Templates (Vorlagen-Engine)
  templates: {
    all: ['templates'] as const,
    list: (filters?: Record<string, unknown>) => ['templates', 'list', filters] as const,
    detail: (id: string) => ['templates', 'detail', id] as const,
    versions: (id: string) => ['templates', 'versions', id] as const,
    version: (templateId: string, versionId: string) => ['templates', 'version', templateId, versionId] as const,
    placeholders: (type: string) => ['templates', 'placeholders', type] as const,
  },

  // Branding
  branding: {
    all: ['branding'] as const,
    detail: () => ['branding', 'detail'] as const,
  },

  // Saved Searches & Query Builder
  savedSearches: {
    all: ['saved-searches'] as const,
    list: (entityType?: string) => ['saved-searches', 'list', entityType] as const,
    detail: (id: string) => ['saved-searches', 'detail', id] as const,
  },

  searchFields: {
    all: ['search-fields'] as const,
    byEntity: (entityType: string) => ['search-fields', entityType] as const,
  },

  resultProfiles: {
    all: ['result-profiles'] as const,
    byEntity: (entityType: string) => ['result-profiles', entityType] as const,
  },
} as const
